import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Users, Swords, Heart, Clock, MapPin, Plus, UserPlus, Lock, Globe, MessageCircle, ChevronRight, Link2 } from "lucide-react";
import { TEST_IDS } from "@/constants/testIds";
import { toast } from "sonner";
import { motion } from "framer-motion";
import AiMatchSuggestions from "@/components/AiMatchSuggestions";
import MatchResultDialog from "@/components/MatchResultDialog";

export default function Matches() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [mode, setMode] = useState("all");
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    club_id: "", date: "", time: "", mode: "friendly",
    skill_level: "all", visibility: "open",
  });

  const fetchMatches = async () => {
    try {
      const params = {};
      if (mode !== "all") params.mode = mode;
      if (mode === "all") params.status = "open,full";
      const res = await api.get("/matches", { params });
      setMatches(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMatches();
    api.get("/club").then(r => { setClubs([r.data]); setForm(f => ({ ...f, club_id: r.data._id })); }).catch(() => {});
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async () => {
    try {
      const club = clubs.find(c => c._id === form.club_id);
      await api.post("/matches", { ...form, club_name: club?.name || "" });
      toast.success("Match created!");
      setCreateOpen(false);
      fetchMatches();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const handleJoin = async (e, matchId) => {
    e.stopPropagation();
    try {
      await api.post(`/matches/${matchId}/join`);
      toast.success("Joined match!");
      fetchMatches();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const copyMatchLink = (e, matchId) => {
    e.stopPropagation();
    const url = `${window.location.origin}/matches/${matchId}`;
    navigator.clipboard.writeText(url);
    toast.success("Invite link copied! Share it with friends.");
  };

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><div className="animate-spin h-8 w-8 border-2 border-coral rounded-full border-t-transparent" /></div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight mb-1">Find a Match</h1>
            <p className="text-muted-foreground">Join open matches or create your own</p>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button data-testid={TEST_IDS.MATCH_CREATE} className="rounded-full bg-coral hover:bg-coral-600 text-white gap-2">
                <Plus size={16} /> Create Match
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create a Match</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="p-3 rounded-xl bg-muted/30 border border-border/40 text-sm flex items-center justify-between" data-testid="match-venue">
                  <span className="text-muted-foreground">Venue</span>
                  <span className="font-semibold">{clubs[0]?.name || "D&D Padel"} · Central Court</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Date</Label>
                    <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="rounded-xl" />
                  </div>
                  <div>
                    <Label>Time</Label>
                    <Input type="time" value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))} className="rounded-xl" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Mode</Label>
                    <Select value={form.mode} onValueChange={v => setForm(f => ({ ...f, mode: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="friendly">Friendly</SelectItem>
                        <SelectItem value="competitive">Competitive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Visibility</Label>
                    <Select value={form.visibility} onValueChange={v => setForm(f => ({ ...f, visibility: v }))}>
                      <SelectTrigger data-testid="match-visibility-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">
                          <div className="flex items-center gap-1.5"><Globe size={14} /> Open</div>
                        </SelectItem>
                        <SelectItem value="private">
                          <div className="flex items-center gap-1.5"><Lock size={14} /> Private</div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Skill Level</Label>
                  <Select value={form.skill_level} onValueChange={v => setForm(f => ({ ...f, skill_level: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Levels</SelectItem>
                      <SelectItem value="beginner">Beginner</SelectItem>
                      <SelectItem value="intermediate">Intermediate</SelectItem>
                      <SelectItem value="advanced">Advanced</SelectItem>
                      <SelectItem value="pro">Pro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.visibility === "private" && (
                  <p className="text-xs text-muted-foreground bg-muted/30 rounded-lg px-3 py-2">
                    <Lock size={12} className="inline mr-1" />
                    Private matches are only visible to players with the direct link.
                  </p>
                )}
                <Button onClick={handleCreate} className="w-full rounded-full bg-coral hover:bg-coral-600 text-white" data-testid="create-match-submit">
                  Create Match
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* AI Suggestions */}
        <div className="mb-8">
          <AiMatchSuggestions />
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <Tabs value={mode} onValueChange={setMode} data-testid={TEST_IDS.MATCH_MODE_TOGGLE}>
            <TabsList className="rounded-full bg-muted/50 p-1">
              <TabsTrigger value="all" className="rounded-full text-sm">All</TabsTrigger>
              <TabsTrigger value="friendly" className="rounded-full text-sm gap-1"><Heart size={14} /> Friendly</TabsTrigger>
              <TabsTrigger value="competitive" className="rounded-full text-sm gap-1"><Swords size={14} /> Competitive</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Match List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {matches.map((match, i) => {
            const isFull = match.players?.length >= match.max_players;
            const hasJoined = match.players?.some(p => p.id === user?.id);
            const isPrivate = match.visibility === "private";
            return (
              <motion.div
                key={match._id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <Card
                  data-testid={TEST_IDS.MATCH_CARD}
                  className="border-border/50 card-lift cursor-pointer hover:border-coral/30 transition-colors"
                  onClick={() => navigate(`/matches/${match._id}`)}
                >
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-1.5">
                        <Badge className={`rounded-full text-xs ${match.mode === "competitive" ? "bg-coral/10 text-coral border-coral/30" : "bg-teal/10 text-teal border-teal/30"}`}>
                          {match.mode === "competitive" ? <Swords size={12} className="mr-1" /> : <Heart size={12} className="mr-1" />}
                          {match.mode}
                        </Badge>
                        {isPrivate ? (
                          <Badge className="rounded-full text-[10px] bg-golden/10 text-golden border-golden/30 gap-0.5">
                            <Lock size={10} /> Private
                          </Badge>
                        ) : (
                          <Badge className="rounded-full text-[10px] bg-teal/5 text-teal/70 border-teal/20 gap-0.5">
                            <Globe size={10} /> Open
                          </Badge>
                        )}
                      </div>
                      <Badge variant="secondary" className="rounded-full text-xs">
                        {match.skill_level === "all" ? "All Levels" : match.skill_level}
                      </Badge>
                    </div>
                    <h3 className="font-heading font-bold text-base mb-2">{match.club_name || "TBD"}</h3>
                    <div className="space-y-1.5 text-sm text-muted-foreground mb-4">
                      <div className="flex items-center gap-1.5">
                        <Clock size={14} /> {match.date} at {match.time}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Users size={14} /> {match.players?.length || 0}/{match.max_players} players
                      </div>
                      <div className="text-xs">Created by {match.creator_name}</div>
                    </div>
                    {/* Player avatars */}
                    <div className="flex items-center gap-1 mb-4">
                      {match.players?.map((p, j) => (
                        <div key={j} className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-bold border-2 border-background">
                          {p.name?.[0]?.toUpperCase()}
                        </div>
                      ))}
                      {Array.from({ length: match.max_players - (match.players?.length || 0) }).map((_, j) => (
                        <div key={`e-${j}`} className="w-8 h-8 rounded-full border-2 border-dashed border-border flex items-center justify-center text-muted-foreground">
                          <Plus size={12} />
                        </div>
                      ))}
                    </div>

                    <div className="flex items-center gap-2">
                      {!hasJoined && !isFull && (
                        <Button
                          data-testid={TEST_IDS.MATCH_JOIN}
                          onClick={(e) => handleJoin(e, match._id)}
                          className="flex-1 rounded-full bg-teal hover:bg-teal-600 text-white text-sm gap-1.5"
                          size="sm"
                        >
                          <UserPlus size={14} /> Join
                        </Button>
                      )}
                      {hasJoined && (
                        <Badge className="flex-1 justify-center py-2 rounded-full bg-teal/10 text-teal border-teal/30">Joined</Badge>
                      )}
                      {isFull && !hasJoined && (
                        <Badge className="flex-1 justify-center py-2 rounded-full" variant="secondary">Full</Badge>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full gap-1 text-xs shrink-0"
                        onClick={(e) => { e.stopPropagation(); navigate(`/matches/${match._id}`); }}
                        data-testid="match-chat-btn"
                      >
                        <MessageCircle size={13} /> Chat
                        <ChevronRight size={13} />
                      </Button>
                      {isPrivate && match.creator_id === user?.id && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-full gap-1 text-xs shrink-0 border-golden/30 text-golden hover:bg-golden/10"
                          onClick={(e) => copyMatchLink(e, match._id)}
                          data-testid="match-share-link-btn"
                        >
                          <Link2 size={13} /> Invite
                        </Button>
                      )}
                    </div>

                    {isFull && match.status !== "completed" && (
                      <div className="mt-2" onClick={e => e.stopPropagation()}>
                        <MatchResultDialog match={match} onSubmitted={fetchMatches} />
                      </div>
                    )}
                    {match.status === "completed" && match.result && (
                      <div className="mt-2 p-2 rounded-xl bg-golden/5 border border-golden/15 text-center">
                        <p className="text-xs font-semibold text-golden">Final Score</p>
                        <p className="text-lg font-black">{match.result.team1_score} - {match.result.team2_score}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {matches.length === 0 && (
          <div className="text-center py-20">
            <Users size={48} className="mx-auto mb-4 text-muted-foreground/30" />
            <p className="text-muted-foreground text-lg mb-2">No matches available</p>
            <p className="text-muted-foreground text-sm">Be the first to create one!</p>
          </div>
        )}
      </motion.div>
    </div>
  );
}
